"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getUserLocation } from "@/utils/location";

export interface UserLocation {
  lat: number;
  lng: number;
}

interface LocationContextValue {
  userLocation: UserLocation | null;
  locationPermissionDenied: boolean;
  showLocationPrompt: boolean;
  handleRequestLocation: () => Promise<void>;
}

const LocationContext = createContext<LocationContextValue | undefined>(
  undefined,
);

export function LocationProvider({ children }: { children: ReactNode }) {
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationPermissionDenied, setLocationPermissionDenied] =
    useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const requestedRef = useRef(false);

  // Resolve the user's location exactly once for the whole app so consumers
  // (properties, managed properties, weekly deals, ...) never re-request the
  // browser geolocation every time a page mounts.
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;

    const checkLocationPermission = async () => {
      try {
        const position = await getUserLocation();
        setUserLocation({ lat: position.lat, lng: position.lng });
        setLocationPermissionDenied(false);
      } catch (error: any) {
        if (error.message === "Location permission denied") {
          setLocationPermissionDenied(true);
          setShowLocationPrompt(true);
        }
      }
    };
    checkLocationPermission();
  }, []);

  const handleRequestLocation = useCallback(async () => {
    try {
      const position = await getUserLocation();
      setUserLocation({ lat: position.lat, lng: position.lng });
      setLocationPermissionDenied(false);
      setShowLocationPrompt(false);
    } catch (error: any) {
      setLocationPermissionDenied(true);
    }
  }, []);

  const value: LocationContextValue = {
    userLocation,
    locationPermissionDenied,
    showLocationPrompt,
    handleRequestLocation,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation(): LocationContextValue {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocation must be used within LocationProvider");
  return ctx;
}
